/* ---------------------------------------------------------------------------
 * WebAuthn / passkey client helpers
 * ---------------------------------------------------------------------------
 * Biometric sign-in (Face ID, Touch ID, Windows Hello, Android fingerprint) is
 * driven by the platform via the WebAuthn API. This module wraps the standard
 * two-call dance for both *registration* (enrol a passkey) and *authentication*
 * (sign in with one):
 *
 *   1. ask the server for options (challenge, rp, allowed credentials…)
 *   2. hand them to navigator.credentials.create()/get() (the OS prompt)
 *   3. send the credential/assertion back for the server to verify
 *
 * The server endpoints are scaffolded in the mock API (see mock-api/routes/
 * auth.js). The production backend must implement real cryptographic
 * verification of the attestation/assertion against the stored public key.
 * ------------------------------------------------------------------------- */

const API = process.env.REACT_APP_API_URL;

/** Is this device/browser capable of WebAuthn passkeys? */
export function isPasskeySupported() {
  return typeof window !== 'undefined' && Boolean(window.PublicKeyCredential);
}

/* ----- base64url <-> ArrayBuffer helpers --------------------------------- */
function bufferToBase64Url(buffer) {
  const bytes = new Uint8Array(buffer);
  let str = '';
  for (let i = 0; i < bytes.length; i += 1) str += String.fromCharCode(bytes[i]);
  return window.btoa(str).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

function base64UrlToBuffer(base64url) {
  const pad = '='.repeat((4 - (base64url.length % 4)) % 4);
  const base64 = (base64url + pad).replace(/-/g, '+').replace(/_/g, '/');
  const str = window.atob(base64);
  const bytes = new Uint8Array(str.length);
  for (let i = 0; i < str.length; i += 1) bytes[i] = str.charCodeAt(i);
  return bytes.buffer;
}

async function getJson(path) {
  const res = await fetch(`${API}${path}`, {
    method: 'GET',
    credentials: 'include',
  });
  if (!res.ok) {
    const err = new Error(`Request to ${path} failed`);
    err.status = res.status;
    throw err;
  }
  return res.json();
}

async function postJson(path, body) {
  const res = await fetch(`${API}${path}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify(body || {}),
  });
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    const err = new Error(text || `Request to ${path} failed`);
    err.status = res.status;
    throw err;
  }
  return res.json();
}

async function sendJson(method, path, body) {
  const res = await fetch(`${API}${path}`, {
    method,
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: body ? JSON.stringify(body) : undefined,
  });
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    const err = new Error(text || `Request to ${path} failed`);
    err.status = res.status;
    throw err;
  }
  return res.json();
}

/* ----- Sign in with an existing passkey ---------------------------------- */
/**
 * @param {string} [email] optional — when omitted a discoverable (usernameless)
 *   credential is requested.
 * @returns {Promise<object>} the server verify response (e.g. { ok, verified, … })
 */
export async function signInWithPasskey(email) {
  if (!isPasskeySupported()) {
    const err = new Error('Passkeys are not supported on this device.');
    err.code = 'unsupported';
    throw err;
  }

  const options = await postJson('/Authentication/passkey/login/options', {
    email: email || null,
  });

  const publicKey = {
    ...options,
    challenge: base64UrlToBuffer(options.challenge),
    allowCredentials: (options.allowCredentials || []).map((cred) => ({
      ...cred,
      id: base64UrlToBuffer(cred.id),
    })),
  };

  const assertion = await navigator.credentials.get({ publicKey });
  if (!assertion) {
    const err = new Error('No assertion returned.');
    err.code = 'no_assertion';
    throw err;
  }

  const payload = {
    id: assertion.id,
    rawId: bufferToBase64Url(assertion.rawId),
    type: assertion.type,
    response: {
      clientDataJSON: bufferToBase64Url(assertion.response.clientDataJSON),
      authenticatorData: bufferToBase64Url(assertion.response.authenticatorData),
      signature: bufferToBase64Url(assertion.response.signature),
      userHandle: assertion.response.userHandle
        ? bufferToBase64Url(assertion.response.userHandle)
        : null,
    },
  };

  return postJson('/Authentication/passkey/login/verify', {
    email: email || null,
    assertion: payload,
  });
}

/* ----- Register a new passkey (for Settings → Security) ------------------- */
/**
 * @param {{ email?: string, displayName?: string }} [opts]
 * @returns {Promise<object>} the server verify response
 */
export async function registerPasskey(opts = {}) {
  const { email, displayName } = opts;
  if (!isPasskeySupported()) {
    const err = new Error('Passkeys are not supported on this device.');
    err.code = 'unsupported';
    throw err;
  }

  const options = await postJson('/Authentication/passkey/register/options', {
    email: email || null,
    displayName: displayName || null,
  });

  const publicKey = {
    ...options,
    challenge: base64UrlToBuffer(options.challenge),
    user: {
      ...options.user,
      id: base64UrlToBuffer(options.user.id),
    },
    excludeCredentials: (options.excludeCredentials || []).map((cred) => ({
      ...cred,
      id: base64UrlToBuffer(cred.id),
    })),
  };

  const credential = await navigator.credentials.create({ publicKey });
  if (!credential) {
    const err = new Error('No credential created.');
    err.code = 'no_credential';
    throw err;
  }

  const payload = {
    id: credential.id,
    rawId: bufferToBase64Url(credential.rawId),
    type: credential.type,
    response: {
      clientDataJSON: bufferToBase64Url(credential.response.clientDataJSON),
      attestationObject: bufferToBase64Url(credential.response.attestationObject),
    },
  };

  return postJson('/Authentication/passkey/register/verify', {
    email: email || null,
    credential: payload,
  });
}

/* ----- Manage enrolled passkeys (Settings → Security) -------------------- */
/**
 * List the signed-in user's registered passkeys.
 * @returns {Promise<Array<{ id, name, createdAt, lastUsedAt }>>}
 */
export async function listPasskeys() {
  const data = await getJson('/Authentication/passkey/list');
  return Array.isArray(data) ? data : [];
}

/** Rename a registered passkey. */
export async function renamePasskey(id, name) {
  return sendJson('PATCH', `/Authentication/passkey/${encodeURIComponent(id)}`, { name });
}

/** Remove / revoke a registered passkey. */
export async function deletePasskey(id) {
  return sendJson('DELETE', `/Authentication/passkey/${encodeURIComponent(id)}`);
}

/* ----- Post-login enrolment prompt -------------------------------------- */
/* Whether the signed-in user already has a passkey on file (session-scoped on
 * the server). */
export async function getPasskeyStatus() {
  try {
    return await getJson('/Authentication/passkey/status');
  } catch (e) {
    return { hasPasskey: false };
  }
}

const SNOOZE_KEY = 'prostay.passkeyPrompt.snoozeUntil';
const NEVER_KEY = 'prostay.passkeyPrompt.neverAsk';

/** Snooze the enrolment prompt for `days` (default 3). */
export function snoozePasskeyPrompt(days = 3) {
  try {
    const until = Date.now() + days * 24 * 60 * 60 * 1000;
    window.localStorage.setItem(SNOOZE_KEY, String(until));
  } catch (e) { /* ignore */ }
}

/** Permanently suppress the enrolment prompt on this device. */
export function suppressPasskeyPrompt() {
  try {
    window.localStorage.setItem(NEVER_KEY, '1');
  } catch (e) { /* ignore */ }
}

function isPromptSuppressed() {
  try {
    if (window.localStorage.getItem(NEVER_KEY) === '1') return true;
    const until = Number(window.localStorage.getItem(SNOOZE_KEY) || 0);
    return until > Date.now();
  } catch (e) {
    return false;
  }
}

/**
 * Decide whether to nudge the user to enrol a passkey after login:
 * supported device + no passkey yet + not snoozed/suppressed.
 */
export async function shouldPromptPasskeyEnrollment() {
  if (!isPasskeySupported()) return false;
  if (isPromptSuppressed()) return false;
  // Platform authenticator (Face ID / Touch ID / Windows Hello) available?
  try {
    if (window.PublicKeyCredential
      && window.PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable) {
      const ok = await window.PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable();
      if (!ok) return false;
    }
  } catch (e) { /* fall through */ }
  const status = await getPasskeyStatus();
  return !status.hasPasskey;
}
